import { useState } from 'react';
import { Check, ExternalLink, Loader2, X } from 'lucide-react';
import { useKnowledgeRecommendations } from '@/hooks/knowledge/useKnowledgeRecommendations';
import { cn } from '@/lib/utils';
import type { KnowledgeRecommendation } from "@/types/knowledge/knowledge-recommendations";
import { GenieIcon } from "@/components/ui/genie-icons/GenieIcon";

interface KnowledgeGeniusCardProps {
  /** Scope the recommendations to one Genie; omit for the workspace view. */
  agentId?: string;
}

const KIND_LABELS: Record<string, { label: string; className: string }> = {
  source_changed: { label: "Website changed", className: "bg-genie-primary/10 text-genie-primary" },
  source_unreachable: { label: "Page unreachable", className: "bg-genie-secondary/10 text-genie-secondary" },
  gap: { label: "Unanswered questions", className: "bg-genie-secondary/10 text-genie-secondary" },
  downstream_stale: { label: "Needs a refresh", className: "bg-gray-100 text-gray-600" },
};

const RecommendationRow = ({
  recommendation,
  onAccept,
  onDismiss,
  isAccepting,
  isDismissing,
}: {
  recommendation: KnowledgeRecommendation;
  onAccept: () => void;
  onDismiss: () => void;
  isAccepting: boolean;
  isDismissing: boolean;
}) => {
  const kindMeta = KIND_LABELS[recommendation.kind] || KIND_LABELS.downstream_stale;
  const highlights =
    recommendation.detail?.highlights ??
    recommendation.detail?.questions?.map((q) => q.question) ??
    [];
  const sourceName = recommendation.documents?.name;
  const url = recommendation.detail?.url;
  const canApply = recommendation.kind === "source_changed";
  const busy = isAccepting || isDismissing;

  return (
    <div className="rounded-xl border border-gray-200 p-4">
      <div className="flex flex-wrap items-center gap-2 mb-2">
        <span className={cn("inline-flex items-center rounded-full px-2 py-1 text-xs font-medium", kindMeta.className)}>
          {kindMeta.label}
        </span>
        {sourceName && (
          <span className="text-xs font-semibold text-gray-900 truncate">{sourceName}</span>
        )}
        {url && (
          <a
            href={url}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 text-xs text-genie-primary hover:underline"
          >
            View page
            <ExternalLink className="h-3 w-3" />
          </a>
        )}
        <span className="ml-auto text-xs text-gray-400">
          {new Date(recommendation.created_at).toLocaleDateString()}
        </span>
      </div>

      <p className="text-sm text-gray-700 leading-relaxed">{recommendation.summary}</p>

      {highlights.length > 0 && (
        <ul className="mt-2 space-y-1">
          {highlights.slice(0, 4).map((highlight, index) => (
            <li key={index} className="flex items-start gap-2 text-xs text-gray-600">
              <span className="mt-1 h-1 w-1 flex-shrink-0 rounded-full bg-genie-primary" />
              <span className="min-w-0">{highlight}</span>
            </li>
          ))}
        </ul>
      )}

      <div className="mt-3 flex items-center gap-2">
        <button
          type="button"
          onClick={onAccept}
          disabled={busy}
          className="inline-flex items-center gap-1.5 rounded-lg bg-gradient-to-r from-genie-primary to-genie-secondary px-3.5 py-1.5 text-xs font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-50"
        >
          {isAccepting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
          {canApply ? "Update my Genie" : "Got it"}
        </button>
        <button
          type="button"
          onClick={onDismiss}
          disabled={busy}
          className="inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium text-gray-500 transition-colors hover:bg-gray-100 disabled:opacity-50"
        >
          {isDismissing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <X className="h-3.5 w-3.5" />}
          Dismiss
        </button>
      </div>
    </div>
  );
};

/**
 * Knowledge Genius: the surface for the autonomous knowledge watcher. A nightly
 * sweep hash-checks every website source against what the Genie knows; changes
 * are applied automatically and land here (and in a digest email) as a receipt.
 */
export const KnowledgeGeniusCard = ({ agentId }: KnowledgeGeniusCardProps) => {
  const {
    recommendations,
    isLoading,
    accept,
    acceptingId,
    dismiss,
    dismissingId,
  } = useKnowledgeRecommendations(agentId);
  const [expanded, setExpanded] = useState(false);

  const visible = expanded ? recommendations : recommendations.slice(0, 3);

  return (
    <div className="mb-6 rounded-2xl border border-gray-200 bg-white overflow-hidden">
      <div className="border-b border-gray-200 p-4 md:p-5">
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0">
            <GenieIcon
              name="globe"
              size={28}
              color="#4E9CFF"
              secondaryColor="#FF4DB8"
              gradientEnabled
              label="Knowledge Genius"
            />
          </div>
          <div className="min-w-0">
            <h2 className="text-base font-bold text-gray-900 leading-snug">Knowledge Genius</h2>
            <p className="text-xs text-gray-600 mt-1 leading-relaxed">
              Your Genie checks your website sources for changes and updates itself automatically,
              so its knowledge never drifts out of date.
            </p>
          </div>
        </div>
      </div>

      <div className="p-4 md:p-5">
        {isLoading ? (
          <div className="flex items-center gap-2 py-4 text-sm text-gray-500">
            <Loader2 className="h-4 w-4 animate-spin text-genie-primary" />
            Checking for recommendations...
          </div>
        ) : recommendations.length === 0 ? (
          <p className="py-2 text-sm text-gray-600">
            All quiet — your website sources match what your Genie knows. New changes will show up
            here and in your email.
          </p>
        ) : (
          <div className="space-y-3">
            {visible.map((recommendation) => (
              <RecommendationRow
                key={recommendation.id}
                recommendation={recommendation}
                onAccept={() => accept(recommendation.id)}
                onDismiss={() => dismiss(recommendation.id)}
                isAccepting={acceptingId === recommendation.id}
                isDismissing={dismissingId === recommendation.id}
              />
            ))}
            {recommendations.length > 3 && (
              <button
                type="button"
                onClick={() => setExpanded((current) => !current)}
                className="text-xs font-medium text-genie-primary hover:underline"
              >
                {expanded ? "Show fewer" : `Show all ${recommendations.length}`}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default KnowledgeGeniusCard;
